import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { ProgressBar } from "@/components/Brand";
import { GraduationCap, Clock, BookOpen, Lock, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

function CourseCard({ course, onOpen }) {
  const started = course.progress_pct > 0;
  return (
    <button
      onClick={() => onOpen(course.id)}
      data-testid={`course-card-${course.id}`}
      className="card-surface group overflow-hidden text-left transition-all hover:shadow-md hover:-translate-y-0.5"
    >
      <div className="relative h-40 w-full overflow-hidden bg-secondary">
        {course.cover_image ? (
          <img src={course.cover_image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid h-full place-items-center text-muted-foreground"><GraduationCap className="h-8 w-8" /></div>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-0.5 text-xs font-medium text-white backdrop-blur">
          {course.category}
        </span>
        {course.sequential_unlock && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] text-white backdrop-blur">
            <Lock className="h-3 w-3" /> Sequential
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-[18px] font-semibold leading-snug">{course.title}</h3>
        <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{course.description}</p>

        <div className="mt-4 flex items-center gap-4 text-[13px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4" /><span className="tnum">{course.lesson_count}</span> lessons</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /><span className="tnum">{course.cpd_hours}</span> CPD hrs</span>
        </div>

        <div className="mt-4">
          <div className="mb-1.5 flex items-center justify-between text-[13px]">
            <span className={cn("font-medium", course.is_complete ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
              {course.is_complete ? "Complete" : started ? "In progress" : "Not started"}
            </span>
            <span className="tnum font-semibold text-[#1779AC] dark:text-[#5cc7ef]">{course.progress_pct}%</span>
          </div>
          <ProgressBar value={course.progress_pct} />
        </div>
      </div>
    </button>
  );
}

export default function Classroom() {
  const [courses, setCourses] = useState(null);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => setCourses([]));
  }, []);

  const filtered = useMemo(() => {
    if (!courses) return null;
    const term = q.trim().toLowerCase();
    if (!term) return courses;
    return courses.filter((c) => (c.title + " " + c.description + " " + c.category).toLowerCase().includes(term));
  }, [courses, q]);

  return (
    <div className="fade-in">
      <PageHeader title="Classroom" subtitle="Work through your curriculum and earn CPD hours." />

      <div className="relative mb-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="classroom-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search courses…"
          className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {filtered === null ? (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No courses found" description="Try a different search, or check back soon." />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="courses-grid">
          {filtered.map((c) => <CourseCard key={c.id} course={c} onOpen={(id) => navigate(`/classroom/${id}`)} />)}
        </div>
      )}
    </div>
  );
}
